import { SweetAlertIcon } from "sweetalert2";

export interface FilterHorarios {
  data: string;
  origem: string;
  destino: string;
  contexto: string;
}

export enum DocumentoPreferencial {
  CartaoEmbarque = "1",
  BPe = "2",
  Voucher = "3",
}

export interface ViacaoAlerta {
  title: string;
  mensagem: string;
  icon: SweetAlertIcon;
  confirmButtonText: string;
}

export interface Viacao {
  id: string;
  nome: string;
  logo: string;
  documento_preferencial: DocumentoPreferencial[];
  documento_preferencial_label: string;
  alerta: ViacaoAlerta | null
  taxa_gopass: number;
  inserir_seguro: boolean;
  devolucao_presencial?: boolean;
}

export interface Passagem {
  identificador: string;
  origem: string;
  origemLabel: string;
  cidadeOrigemId: string;
  cidadeDestinoId: string;
  destino: string;
  destinoLabel: string;
  dataViagem: string;
  servico: string;
  grupo: string;
  saidaDia: string;
  saidaHora: string;
  chegadaHora: string;
  chegadaDia: string;
  duracao: string;
  desembarque: string;
  livres: string;
  empresa: string;
  classe: string;
  precoBase: number;
  preco: number;
  precoSemViagemPremiada?: number;
  viacao: Viacao;
  valorSeguro: number;
  valorViagemPremiada?: number;
  taxaAgencia: string;
  taxaGoPass: string;
  adaptador: string;
  provedorWebviagem?: number | null;
  embarqueDireto: boolean;
  contexto: 'ida' | 'volta';
  conexoes: Passagem[]
}

export interface AvisoPoltrona {
  tipo?: string;
  codigo?: string;
  mensagem?: string;
  valor_base_busca?: number;
  valor_base_reserva?: number;
  valor_busca?: number;
  valor_atual?: number;
  valor_reserva?: number;
  assento?: string;
  bilhete_id?: string | number;
  pedido_id?: string | number;
}

export interface Assento {
  x: string;
  y: string;
  z: string;
  ocupado: number;
  tipo: string;
  assento: string;
  servico: string;
  aviso?: string;
  avisos?: AvisoPoltrona[];
}

export interface Passageiro {
  id: string;
  nome: string;
  sobrenome: string;
  documento: string;
  data_nascimento: string;
  telefone: string;
}

export interface Cidade {
  id: string;
  nome: string;
  estado: string;
  endereco_embarque?: string | null;
  rodon_codigo: string
  buson_codigo: string
}

export type BilheteStatus = 'RESERVADO' | 'CONFIRMADO' | 'CANCELADO' | 'EXPIRADO' | 'INVALIDO' | 'DEVOLVIDO' | 'ESTORNO_PENDENTE'

export const enum BilheteStatusCode {
  Reservado = 1,
  Confirmado = 2,
  Cancelado = 3,
  Expirado = 4,
  Invalido = 5,
  Devolvido = 6,
  EstornoPendente = 7
}

export interface Bilhete {
  id: string;
  viacao: Viacao;
  passageiro: Passageiro;
  cidade_origem: Cidade;
  cidade_destino: Cidade;
  assento: string;
  valor_total: number;
  multa?: number | string;
  multa_cancelamento?: number | string;
  adapter?: string;
  taxa_sistem: number;
  valor_seguro: number;
  viagem_premiada?: number;
  comprovante_bpe: string;
  classe: string;
  embarque: string;
  id_transacao: string;
  grupo: string;
  informacoes: string;
  valor_bilhete: string;
  created_at: string;
  updated_at: string;
  expires_at: string;
  is_expired: boolean;
  documento_embarque: string;
  endereco_embarque?: string | null;
  adaptador: string;
  provedorWebviagem?: number | null;
  servico: string;
  localizador: string;
  numero_pedido: string;
  pedido_id: string;
  seguro: boolean
  viagem_premiada_contratada?: boolean;
  contexto: 'ida' | 'volta',
  status: number;
  status_label: BilheteStatus;
  devolucao_bloqueada?: boolean;
  devolucao_presencial?: boolean;
}

export interface Pix {
  id: string;
  valor?: number;
  dados_qrcode: string;
  qrCode: string;
  data_criacao: string,
  data_expiracao: string,
  status?: number;
  status_label?: string;
  tipo?: 'fatura' | 'total_caixa';
  fatura_ids?: number[];
}

export type CheckoutProgressStep =
  | "generating_pix"
  | "generating_link"
  | "waiting_payment"
  | "registering_payment"
  | "payment_registered"
  | "confirming_order"
  | "order_confirmed"
  | "finalizing_order"
  | "completed"
  | "failed";

export interface CheckoutProgress {
  pedido_id: number;
  step: CheckoutProgressStep;
  label: string;
  description: string;
  status: "in_progress" | "completed" | "failed";
  method: "pix" | "cartao" | "dinheiro" | "link" | "pos" | "desconhecido";
  updated_at: string | null;
  error_message: string | null;
  redirect_to: string | null;
}

export type StatusPedido =
  | 'CANCELADO'
  | 'ABERTO'
  | 'RESERVADO'
  | 'FINALIZADO'
  | 'ESTORNO PENDENTE'
  | 'PROCESSANDO'
  | 'DEVOLVIDO'
  | 'DEVOLVIDO PARCIALMENTE'
  | 'CONTESTADO'

export const enum PedidoStatusCode {
  Aberto = 1,
  Reservado = 2,
  Finalizado = 3,
  Cancelado = 4,
  EstornoPendente = 5,
  Processando = 6,
  Devolvido = 7,
  DevolvidoParcialmente = 8,
  Contestado = 9,
}

export interface Pedido {
  id: string;
  fatura: string;
  erro: string | null;
  bilhetes: Bilhete[];
  valor_total: string;
  valor_base_comissao_representante?: number;
  data_emissao: string;
  forma_pagamento: string;
  status: number;
  status_label: StatusPedido;
  created_at: string;
  pix: Pix | null;
  qnt_bilhetes: number;
  usuario: string;
  agencia: string;
  agencia_id?: number;
  repassar_multa?: boolean;
  forma_pagamento_label: string;
  comissao: number;
  comissao_representante?: number;
  ordem_trabalho?: string;
  usuario_id?: number | string;
  devolucao_presencial?: boolean;
  aviso_devolucao_presencial?: string | null;
}

export interface Emissor {
  nome: string;
  number_of_parcela: number;
  email_financeiro: string;
  status?: number | string;
  pendencia_financeira?: boolean;
  show_ordem_trabalho?: boolean;
  pagamento_faturado_habilitado?: boolean;
  pagamento_pix_habilitado?: boolean;
  pagamento_cartao_credito_habilitado?: boolean;
  webpag_link_pagamento_habilitado?: boolean;
  webpag_pos_habilitado?: boolean;
}

export type PagamentoLinkStatus = "aberto" | "pago" | "reembolsado" | "cancelado" | "contestado";

export interface PagamentoLink {
  id: number;
  id_webpag: string | null;
  pedido: number;
  valor: number;
  link: string;
  status: PagamentoLinkStatus;
  status_label: string;
  created_at: string;
  updated_at: string;
  expira_em: string;
  expirado: boolean;
}

export interface MetaPagination {
  current_page: number;
  last_page: number;
  per_page: number;
  to: number;
  total: number;
}

export interface Fatura {
  id: string;
  codigo: string;
  status_label: 'Vencida' | 'Aberta' | 'Paga' | 'Fechada';
  data_vencimento: string;
  data_fechamento: string;
  valor_total: number;
  created_at: string;
  pix: Pix;
  pix_pagamento?: Pix | null;
  pixes?: Pix[];
  pixes_parciais?: Pix[];
  pix_total_caixa?: Pix | null;
  valor_pago?: number;
  valor_pendente?: number;
  valor_pix_pagamento?: number;
  boleto_id: number;
  boleto_sicoob_id?: number | null;
  boleto_sicoob_habilitado?: boolean;
  boleto_sicoob?: {
    id: number;
    linha_digitavel?: string | null;
    qr_code?: string | null;
    qr_code_image?: string | null;
    valor?: number | null;
    status_label?: string | null;
  } | null;
  pix_id: number;
}

export interface Usuario {
  id: string;
  nome: string;
  perfil: string;
  status: number;
  empresa: string;
  email: string;
  cpf: string;
  tipo: number;
}

export interface ContaCorrente {
  id: string;
  info: string;
  saldo: number;
  updated_at: string;
}

export interface MovimentacaoFinanceira {
  id: string;
  valor: number;
  valor_taxa: number;
  valor_bruto: number;
  valor_conveniencia: number;
  saldo: number;
  usuario: string;
  created_at: string;
  info: string;
  tipo: "C" | "D"
}

export interface AgenciaAfiliada {
  id: number;
  nome: string;
  taxa_agencia: number;
  taxa_gopass: number;
  taxa_representante: number;
}
